/**
 * StepProgress - 顶部步骤进度条
 * 显示三步进度，支持点击已完成的步骤返回
 */
import { motion } from 'framer-motion';
import { User, BookOpen, Palette, Check } from 'lucide-react';
import { CreationStep } from '../../../store/useCreationStore';

interface StepProgressProps {
  currentStep: CreationStep;
  onStepClick: (step: number) => void;
}

const steps = [
  { id: 1, label: '角色设定', icon: User },
  { id: 2, label: '故事蓝图', icon: BookOpen },
  { id: 3, label: '视觉风格', icon: Palette },
];

export default function StepProgress({ currentStep, onStepClick }: StepProgressProps) {
  return (
    <div className="flex items-center justify-center max-w-2xl mx-auto">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        const isClickable = step.id <= currentStep;

        return (
          <div key={step.id} className="flex items-center">
            {/* 步骤圆圈 */}
            <motion.button
              onClick={() => isClickable && onStepClick(step.id)}
              disabled={!isClickable}
              whileHover={isClickable ? { scale: 1.05 } : {}}
              whileTap={isClickable ? { scale: 0.95 } : {}}
              className={`
                relative flex flex-col items-center gap-2 group
                ${!isClickable && 'cursor-not-allowed'}
              `}
            >
              {/* 图标容器 */}
              <div
                className={`
                  w-12 h-12 rounded-2xl flex items-center justify-center
                  transition-all duration-300 shadow-sm
                  ${isCompleted
                    ? 'bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-amber-200'
                    : isCurrent
                    ? 'bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-indigo-200 shadow-lg'
                    : 'bg-slate-100 text-slate-400 border-2 border-slate-200'
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}

                {/* 当前步骤脉冲效果 */}
                {isCurrent && (
                  <span className="absolute inset-0 rounded-2xl bg-indigo-500/20 animate-ping" />
                )}
              </div>

              {/* 标签 */}
              <span
                className={`
                  text-sm font-medium transition-colors duration-300
                  ${isCompleted || isCurrent
                    ? 'text-slate-800'
                    : 'text-slate-400'
                  }
                `}
              >
                {step.label}
              </span>
            </motion.button>

            {/* 连接线 */}
            {index < steps.length - 1 && (
              <div className="w-24 sm:w-32 h-1 mx-2 sm:mx-4 relative">
                {/* 背景线 */}
                <div className="absolute inset-0 bg-slate-200 rounded-full" />
                {/* 进度线 */}
                <motion.div
                  initial={false}
                  animate={{
                    scaleX: isCompleted ? 1 : 0,
                  }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                  className="absolute inset-0 bg-gradient-to-r from-amber-400 to-amber-500 rounded-full origin-left"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
